import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { addQueryParam, removeQueryParam } from 'client/helpers/queryParamHelpers';
import moment from 'moment/moment';
import lodashCompact from 'lodash/fp/compact';
import lodashWithout from 'lodash/fp/without';
import lodashUniq from 'lodash/fp/uniq';
import classNames from 'classnames';

import {
  isHomebuyerPartner,
  isUtilityItemDropoutByRenter,
  shouldViewTenantLiabilityBanners,
} from 'common/utility/helpers';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityPartnerActions from 'client/actions/UtilityPartner';
import useDispatch from 'client/hooks/useDispatch';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
  SR_API_ACTION_UTILITY_ACTIVATION_TABLE_FILTERS_COUNT,
  SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
} from 'api/utilityActionTypes';

import { DATA_TABLE_SOURCE_UTILITY_ACTIVATIONS } from 'common/dataTableConstants';
import {
  UTILITY_ACTIVATION_STATUS_PENDING,
  UTILITY_ACTIVATION_STATUS_MISSING_PROVIDER,
  UTILITY_ACTIVATION_STATUS_COMPLETED,
  UTILITY_ACTIVATION_STATUS_CANCEL_REASON_OTHER,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
  UTILITY_TYPE_ID_ELECTRICITY,
} from 'common/utility/constants';
import { makeAddressCityStateString } from '@utilityprofit/address-helpers';
import { formatPhoneNumber } from 'common/formatters';

import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrTooltip from 'client/components/SrTooltip';
import SrUtilityActivationStatusModal from 'client/components/SrUtilityActivationStatusModal/SrUtilityActivationStatusModal';
import SrUtilityActivationCancelModal from 'client/components/SrUtilityActivationCancelModal/SrUtilityActivationCancelModal';
import UPPropertyManagementSoftwareBanner from 'client/components/UPPropertyManagementSoftwareBanner/UPPropertyManagementSoftwareBanner';
import UPTenantLiabilityBannerRow from 'client/components/UPTenantLiabilityBanner/UPTenantLiabilityBannerRow';
import UPEnableTenantLiabilityModal from 'client/components/UPEnableTenantLiabilityModal/UPEnableTenantLiabilityModal';
import SrUtilityProfitActivationsDataTableHeader, {
  MOVE_IN_DATE_FILTER_ALL,
  MOVE_IN_DATE_FILTER_UPCOMING,
} from 'client/components/SrUtilityProfitActivationsDataTable/SrUtilityProfitActivationsDataTableHeader';

const PMS_BANNER_POLL_INTERVAL_MS = 60000;

const STATIC_HEADERS = [
  {
    content: 'Address',
    style: { width: '300px' },
  },
];

const ACTIVATIONS_COUNT = 20;

export function getDataTableSettingsFromProps({ utilityPartnerId, leaseStartDateStatus }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_UTILITY_ACTIVATIONS,
    rootViewId: DATA_TABLE_SOURCE_UTILITY_ACTIVATIONS,
    initialViewSettings: {
      filter: {
        status: [
          UTILITY_ACTIVATION_STATUS_PENDING,
          UTILITY_ACTIVATION_STATUS_MISSING_PROVIDER,
          UTILITY_ACTIVATION_STATUS_COMPLETED,
        ],
        leaseStartDateStatus: leaseStartDateStatus,
        ...(utilityPartnerId ? { utilityPartnerId } : {}),
      },
      ...(utilityPartnerId
        ? {
            customParams: {
              utilityActivations: {
                includeArchived: true,
              },
            },
          }
        : {}),
      sort: {
        leaseStartDate: 'desc',
      },
      includeItemsCount: true,
    },
    count: ACTIVATIONS_COUNT,
    itemsPropName: 'activations',
    resetOnUnmount: true,
  };
  return dataTableSettings;
}

const SrUtilityProfitActivationsDataTable = ({
  showPartner,
  utilityPartner,
  propertyManagementSoftwareList,
  utilityTypes,
  onSelectPropertyManagementSoftware,
}) => {
  // Filter out liability insurance if not enabled in experience configuration
  const filteredUtilityTypes = utilityTypes.filter(
    (type) =>
      type.id !== UTILITY_TYPE_ID_LIABILITY_INSURANCE ||
      utilityPartner?.experienceConfiguration?.liabilityInsuranceEnabled,
  );

  const [viewingActivation, setViewingActivation] = useState(null);
  const [cancellingActivation, setCancellingActivation] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [moveInDateFilter, setMoveInDateFilter] = useState(MOVE_IN_DATE_FILTER_UPCOMING);
  const [tableFiltersCountLoaded, setTableFiltersCountLoaded] = useState(false);
  const [showEnableTenantLiabilityModal, setShowEnableTenantLiabilityModal] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const dispatch = useDispatch();

  const onFilterSelected = ({ filter, viewSettings, count = ACTIVATIONS_COUNT }) => {
    let updatedFilters = viewSettings.filter;
    if (filter === MOVE_IN_DATE_FILTER_ALL || searchInput.length > 0) {
      updatedFilters = Object.keys(updatedFilters).reduce((acc, key) => {
        if (key !== 'leaseStartDateStatus') {
          acc[key] = updatedFilters[key];
        }
        return acc;
      }, {});
    } else {
      updatedFilters = {
        ...viewSettings.filter,
        leaseStartDateStatus: filter,
      };
    }
    setMoveInDateFilter(filter);

    return viewSettings.update(
      {
        ...viewSettings,
        filter: updatedFilters,
        sort: {
          leaseStartDate: 'desc',
        },
        count: count,
      },
      true,
    );
  };

  const parseAddressCell = ({ address, partner, createdAt }) => {
    return (
      <SrText font="greycliff" fontSize="16px" weight="400">
        <SrText weight="800">{address.streetDisplay}</SrText>
        {`  ${makeAddressCityStateString(address)}`}
        <SrBox marginTop="-7px">
          <SrText size="12" weight="500">
            {showPartner && partner ? `Partner: ${partner.name} - ` : null}
            Created {formatDate(createdAt)}
          </SrText>
        </SrBox>
      </SrText>
    );
  };

  const parseTenantCell = (user) => {
    if (!user) {
      return (
        <SrText font="greycliff" fontSize="16px" weight="400">
          Pending Sign Up
        </SrText>
      );
    }

    return (
      <SrTooltip
        className="no-icon bg-color-blue-bright"
        label={
          <SrText font="greycliff" fontSize="16px" weight="400">
            {user.firstName} {user.lastName}
          </SrText>
        }
      >
        <SrText font="greycliff" display="block" weight="800" fontSize="16px">
          {user.firstName} {user.lastName}
        </SrText>
        <SrText font="greycliff" display="block" weight="800" fontSize="16px">
          {formatPhoneNumber(user.phone)}
        </SrText>
        <SrText font="greycliff" display="block" weight="800" fontSize="16px">
          {user.email}
        </SrText>
      </SrTooltip>
    );
  };

  const formatDate = (date) => {
    return moment(date).format('MMM D, YYYY');
  };

  const openArchiveModal = () => {
    setCancellingActivation(viewingActivation);
    setViewingActivation(null);
  };

  const onArchiveSubmit = ({ reloadItems, formValues }) => {
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
      payload: {
        id: cancellingActivation.id,
        updates: {
          archivedAt: moment().toISOString(),
          cancelReason: UTILITY_ACTIVATION_STATUS_CANCEL_REASON_OTHER,
          cancelReasonOther: formValues.cancelReasonOther,
        },
      },
    }).then(() => {
      reloadItems({
        removeEntityId: cancellingActivation.id,
      });
      setCancellingActivation(null);
    });
  };

  const handleRequiredUtilityTypeToggle = ({ reloadItems, utilityTypeId }) => {
    let utilityTypeIds = viewingActivation.requiredTypes.map(({ utilityTypeId }) => utilityTypeId);
    if (utilityTypeIds.includes(utilityTypeId)) {
      utilityTypeIds = lodashWithout([utilityTypeId], utilityTypeIds);
    } else {
      utilityTypeIds = lodashUniq([...utilityTypeIds, utilityTypeId]);
    }

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
      payload: {
        addressId: viewingActivation.address.id,
        utilityTypeIds,
      },
    }).then(async () => {
      const response = await reloadItems({
        count: 1,
        filter: {
          id: viewingActivation.id,
        },
      });
      setViewingActivation(null);
      const updatedActivation = (response.items || []).find(
        (item) => item.id === viewingActivation.id,
      );
      if (updatedActivation) {
        setViewingActivation(updatedActivation);
      }
    });
  };

  const onItemEditCallback = ({ reloadItems }) => {
    return reloadItems({
      count: 1,
      filter: {
        id: viewingActivation.id,
      },
    }).then((response) => {
      const updatedActivation = (response.items || []).pop();
      if (updatedActivation) {
        return setViewingActivation(updatedActivation);
      }
    });
  };

  const onSearchInputChange = ({ loadNextPage, viewSettings, input }) => {
    const searchTerm = input.target.value;
    let updatedFilters = viewSettings.filter;

    if (searchTerm.length === 0) {
      updatedFilters = {
        ...viewSettings.filter,
        leaseStartDateStatus:
          moveInDateFilter === MOVE_IN_DATE_FILTER_ALL ? undefined : moveInDateFilter,
      };
    } else {
      updatedFilters = Object.keys(viewSettings.filter).reduce((acc, key) => {
        if (key !== 'leaseStartDateStatus') {
          acc[key] = viewSettings.filter[key];
        }
        return acc;
      }, {});
    }

    setSearchInput(searchTerm);
    if (viewSettings) {
      viewSettings.update({
        ...viewSettings,
        loadAllData: true,
        fuzzy: !searchTerm
          ? null
          : {
              queryString: searchTerm,
              columns: ['address.display', 'user.firstName', 'user.lastName', 'user.phone'],
            },
        filter: updatedFilters,
      });
      loadNextPage();
    }
  };

  const getActivationItemRowState = useCallback(({ activationItem }) => {
    const completed = Object.freeze({
      icon: 'checkmark-green-bold-active',
      tooltip: {
        text: 'Completed',
        className: 'utility-completed',
      },
    });

    const completedOnSwitch = Object.freeze({
      icon: 'star-verified-utility-setup',
      tooltip: {
        text: 'Verified utility confirmation',
        className: 'utility-onswitch-verified',
      },
    });

    const completedOutside = Object.freeze({
      icon: 'green-checkmark-ghost',
      tooltip: {
        text: 'Completed. Purchased outside of platform',
        className: 'utility-ghost',
      },
    });

    const incompleted = Object.freeze({
      icon: 'clock-icon-orange',
      tooltip: {
        text: 'Incomplete',
        className: 'utility-incompleted',
      },
    });

    const finalizing = Object.freeze({
      icon: 'checkmark-orange-small-active',
      tooltip: {
        text: 'Finalizing Purchase',
        className: 'utility-purchase-requested',
      },
    });

    const resolveCompleted = activationItem.verifiedOrder ? completedOnSwitch : completed;

    if (activationItem.type.id === UTILITY_TYPE_ID_ELECTRICITY) {
      if (activationItem.status === UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED) {
        return resolveCompleted;
      }

      const dropoutByRenter = isUtilityItemDropoutByRenter(activationItem);
      const hasAccountDetails = activationItem.accountNumber || activationItem.activationDate;

      if (dropoutByRenter && hasAccountDetails) {
        return resolveCompleted;
      }

      return incompleted;
    }

    if (activationItem.status === UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED) {
      return resolveCompleted;
    }
    if (activationItem.status === UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED) {
      return finalizing;
    }
    if (activationItem.status === UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT) {
      return completedOutside;
    }
    return incompleted;
  }, []);

  const pmsBannerDOM = utilityPartner && propertyManagementSoftwareList && (
    <SrBox marginTop="20px">
      <UPPropertyManagementSoftwareBanner
        utilityPartner={utilityPartner}
        propertyManagementSoftwareList={propertyManagementSoftwareList}
        onSelectPropertyManagementSoftware={onSelectPropertyManagementSoftware}
      />
    </SrBox>
  );

  const tenantLiabilityBannerDOM = shouldViewTenantLiabilityBanners(utilityPartner) && (
    <SrBox marginTop="8px">
      <UPTenantLiabilityBannerRow
        onEnableClick={() => {
          setShowEnableTenantLiabilityModal(true);
          addQueryParam(history, location, url, 'lm', '1');
        }}
      />
    </SrBox>
  );

  useEffect(() => {
    if (utilityPartner && !tableFiltersCountLoaded) {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_ACTIVATION_TABLE_FILTERS_COUNT,
        payload: {
          utilityPartnerId: utilityPartner.id,
        },
      }).then((result) => {
        if (result.upcomingCount === 0) {
          setMoveInDateFilter(MOVE_IN_DATE_FILTER_ALL);
        }

        setTableFiltersCountLoaded(true);
      });
    } else {
      setTableFiltersCountLoaded(true);
    }
  }, [utilityPartner, dispatch, tableFiltersCountLoaded]);

  const utilityPartnerSlug = utilityPartner?.slug;
  useEffect(() => {
    if (!utilityPartnerSlug) return;

    let cancelled = false;
    let timeoutId = null;

    const poll = async () => {
      if (cancelled) return;

      try {
        await dispatch(UtilityPartnerActions.getBySlug, {
          slug: utilityPartnerSlug,
          forceRefresh: true,
        });
      } finally {
        if (!cancelled) {
          timeoutId = setTimeout(poll, PMS_BANNER_POLL_INTERVAL_MS);
        }
      }
    };

    timeoutId = setTimeout(poll, PMS_BANNER_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [utilityPartnerSlug, dispatch]);

  if (!tableFiltersCountLoaded) {
    return null;
  }

  return (
    <SrDataTableConnected
      {...getDataTableSettingsFromProps({
        utilityPartnerId: utilityPartner?.id,
        leaseStartDateStatus:
          moveInDateFilter === MOVE_IN_DATE_FILTER_ALL ? undefined : MOVE_IN_DATE_FILTER_UPCOMING,
      })}
    >
      {({
        activations,
        activationsCount,
        reloadItems,
        loadNextPage,
        viewSettings,
        dataTableApiState,
        dataTableIsFullyLoaded,
      }) => {
        // eslint-disable-line max-len
        const activationsLoaded = activations.length === activationsCount;

        if (dataTableApiState.inProgress && activations.length === 0) {
          return (
            <div>
              <SrBox width="100%">
                <SrUtilityProfitActivationsDataTableHeader
                  onSearchInputChange={(input) =>
                    onSearchInputChange({
                      viewSettings,
                      loadNextPage,
                      input,
                    })
                  }
                  searchInput={searchInput}
                  moveInDateFilter={moveInDateFilter}
                  setMoveInDateFilter={(filter) => onFilterSelected({ filter, viewSettings })}
                  utilityPartner={utilityPartner}
                />
                {pmsBannerDOM}
                {tenantLiabilityBannerDOM}
              </SrBox>
              <SrBox marginTop="8px" paddingBottom="56px">
                <div>
                  <div className="loading-ellipses clearfix" />
                </div>
              </SrBox>
            </div>
          );
        }

        const dataTableHeaders = [
          ...STATIC_HEADERS,
          {
            content: isHomebuyerPartner(utilityPartner) ? 'Client' : 'Tenant',
          },
          {
            content: isHomebuyerPartner(utilityPartner) ? 'Move in' : 'Lease Start',
            dataTableFieldId: 'leaseStartDate',
            sortPosition: 'right',
          },
          ...filteredUtilityTypes.map(({ id, label }) => ({
            content: id === UTILITY_TYPE_ID_LIABILITY_INSURANCE ? 'Insurance' : label,
            style: {
              textAlign: 'center',
              width: '80px',
            },
          })),
          ...(!isHomebuyerPartner(utilityPartner)
            ? [
                {
                  content: '',
                  style: { width: '16px' },
                },
              ]
            : []),
        ];

        const activationRows = activations.map((activation) => {
          return {
            onClick: isHomebuyerPartner(utilityPartner)
              ? undefined
              : () => setViewingActivation(activation),
            style: isHomebuyerPartner(utilityPartner) ? { cursor: 'default' } : undefined,
            cells: [
              {
                content: parseAddressCell(activation, { createdAt: activation.createdAt }),
                style: {
                  maxWidth: '290px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
              {
                content: parseTenantCell(activation.user),
              },
              {
                content: (
                  <SrText font="greycliff" weight="700">
                    {formatDate(activation.leaseStartDate)}
                  </SrText>
                ),
              },
              ...filteredUtilityTypes.map(({ id }) => {
                const activationItem = activation.items?.find((ai) => {
                  return ai.utilityTypeId === id;
                });

                if (!activationItem) {
                  return {
                    content: (
                      <SrText font="greycliff" color="greyMonochromeInactive" weight="600">
                        N/A
                      </SrText>
                    ),
                    style: { textAlign: 'center' },
                  };
                }

                const {
                  icon: activationItemIcon,
                  tooltip: activationItemTooltip,
                } = getActivationItemRowState({ activationItem });

                return {
                  content: (
                    <SrTooltip
                      className={classNames('no-icon flex-center bg-color-blue-bright', {
                        [activationItemTooltip.className]: true,
                      })}
                      label={
                        <SrBox
                          width="24px"
                          height="24px"
                          margin="0 auto"
                          backgroundSize="contain"
                          backgroundRepeat="no-repeat"
                          backgroundImage={`url(/images/${activationItemIcon}.svg)`}
                        />
                      }
                    >
                      <SrText font="greycliff" weight="800" fontSize="16px">
                        {activationItemTooltip.text}
                      </SrText>
                    </SrTooltip>
                  ),
                  style: { textAlign: 'center' },
                };
              }),
              ...(!isHomebuyerPartner(utilityPartner)
                ? [
                    {
                      content: (
                        <div>
                          <SrBox
                            width="12px"
                            height="12px"
                            backgroundImage="url(/images/blue-right-arrow-listing-row.svg)"
                          />
                        </div>
                      ),
                    },
                  ]
                : []),
            ],
          };
        });

        const loadMoreRow =
          dataTableIsFullyLoaded ||
          activationsLoaded ||
          viewSettings?.fuzzy?.queryString ||
          dataTableApiState.inProgress
            ? null
            : {
                onClick: () => (dataTableApiState.inProgress ? null : loadNextPage()),
                cells: [
                  {
                    content: (
                      <div
                        style={{ height: '100%', textAlign: 'center', backgroundColor: '#F5F9FF' }}
                      >
                        <SrText
                          lineHeight="50px"
                          font="greycliff"
                          weight="800"
                          size="16"
                          color="blue300"
                        >
                          View more activations
                        </SrText>
                      </div>
                    ),
                    colSpan: dataTableHeaders.length,
                    noPadding: true,
                  },
                ],
              };

        const loadAllActivationsRow =
          (dataTableIsFullyLoaded || activationsLoaded) &&
          !dataTableApiState.inProgress &&
          moveInDateFilter !== MOVE_IN_DATE_FILTER_ALL &&
          !loadMoreRow
            ? {
                onClick: () =>
                  onFilterSelected({
                    filter: MOVE_IN_DATE_FILTER_ALL,
                    viewSettings,
                    count: (activationsCount || 0) + ACTIVATIONS_COUNT,
                  }),
                cells: [
                  {
                    content: (
                      <div
                        style={{ height: '100%', textAlign: 'center', backgroundColor: '#F5F9FF' }}
                      >
                        <SrText
                          lineHeight="50px"
                          font="greycliff"
                          weight="800"
                          size="16"
                          color="blue300"
                        >
                          View all past activations
                        </SrText>
                      </div>
                    ),
                    colSpan: dataTableHeaders.length,
                    noPadding: true,
                  },
                ],
              }
            : null;

        return (
          <div>
            <SrBox width="100%">
              <SrUtilityProfitActivationsDataTableHeader
                onSearchInputChange={(input) =>
                  onSearchInputChange({
                    viewSettings,
                    loadNextPage,
                    input,
                  })
                }
                searchInput={searchInput}
                moveInDateFilter={moveInDateFilter}
                setMoveInDateFilter={(filter) => onFilterSelected({ filter, viewSettings })}
                utilityPartner={utilityPartner}
              />
              {pmsBannerDOM}
              {tenantLiabilityBannerDOM}
            </SrBox>
            <SrBox marginTop="8px" paddingBottom="56px">
              <div>
                <SrDataTableUI
                  sticky
                  theme={SR_TABLE_THEME_PARTNER_CLEAR}
                  dataTableViewSettings={viewSettings}
                  headers={dataTableHeaders}
                  maxWidth="100%"
                  rows={lodashCompact([...activationRows, loadMoreRow, loadAllActivationsRow])}
                />
                {dataTableApiState.inProgress && (
                  <div>
                    <div className="loading-ellipses clearfix" />
                  </div>
                )}
              </div>
              {activations.length === 0 &&
                dataTableIsFullyLoaded &&
                moveInDateFilter === MOVE_IN_DATE_FILTER_ALL && (
                  <SrBox
                    backgroundColor="white"
                    display="flex"
                    justifyContent="center"
                    padding="20px"
                  >
                    <SrText font="greycliff" weight="500" fontSize="16px" fontStyle="italic">
                      {searchInput ? 'No search results.' : 'No utility links created yet.'}
                    </SrText>
                  </SrBox>
                )}
              {cancellingActivation && (
                <SrUtilityActivationCancelModal
                  activation={cancellingActivation}
                  onSubmit={(formValues) =>
                    onArchiveSubmit({
                      reloadItems,
                      formValues,
                    })
                  }
                  onClose={() => setCancellingActivation(null)}
                />
              )}
              {viewingActivation && (
                <SrUtilityActivationStatusModal
                  activation={viewingActivation}
                  utilityPartner={utilityPartner}
                  utilityTypes={utilityTypes.filter(
                    (type) =>
                      type.id !== UTILITY_TYPE_ID_LIABILITY_INSURANCE ||
                      utilityPartner?.experienceConfiguration?.liabilityInsuranceEnabled,
                  )}
                  onClose={() => setViewingActivation(null)}
                  onArchive={openArchiveModal}
                  onItemEditCallback={() => onItemEditCallback({ reloadItems })}
                  onRequiredUtilityTypeToggle={(utilityTypeId) =>
                    handleRequiredUtilityTypeToggle({
                      reloadItems,
                      utilityTypeId,
                    })
                  }
                />
              )}
              {showEnableTenantLiabilityModal && (
                <UPEnableTenantLiabilityModal
                  utilityPartner={utilityPartner}
                  onClose={() => {
                    setShowEnableTenantLiabilityModal(false);
                    removeQueryParam(history, location, url, 'lm');
                  }}
                  onSuccess={() => {
                    dispatch(UtilityPartnerActions.getBySlug, {
                      slug: utilityPartner.slug,
                      forceRefresh: true,
                    });
                  }}
                />
              )}
            </SrBox>
          </div>
        );
      }}
    </SrDataTableConnected>
  );
};

SrUtilityProfitActivationsDataTable.propTypes = {
  utilityPartner: PropTypes.object,
  utilityTypes: PropTypes.array.isRequired,
};

SrUtilityProfitActivationsDataTable.displayName = 'SrUtilityProfitActivationsDataTable';

export default SrUtilityProfitActivationsDataTable;
